import React from 'react';
import { CeoPrioritiesChart } from './ReportCharts';
import { aiTable, erosionAreas } from '../../data/mockData';

const PageHeader = ({ page }) => (
  <div className="flex items-center justify-between pb-3 mb-4 text-xs text-muted-foreground border-b border-border">
    <span className="font-display font-semibold tracking-wide">GDO Italia 2025-2030</span>
    <span>Skillvue | Pag. {page}</span>
  </div>
);

const Page5 = () => (
  <div className="px-10 py-8">
    <PageHeader page={5} />
    <h3 className="mb-3 text-base font-bold text-foreground font-display">5.1 Le sei aree di erosione dei margini</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
      {erosionAreas.map((area) => (
        <div key={area.num} className="report-section flex gap-3 rounded-lg bg-secondary/60 p-4">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full gradient-bg text-xs font-bold text-white">
            {area.num}
          </div>
          <div>
            <h5 className="text-xs font-bold text-foreground font-display">{area.title}</h5>
            <p className="text-[11px] leading-relaxed text-muted-foreground mt-0.5">{area.desc}</p>
          </div>
        </div>
      ))}
    </div>

    <h3 className="mt-5 mb-2 text-base font-bold text-foreground font-display">6. L'accelerazione digitale e il paradosso delle competenze</h3>
    <p className="text-sm leading-relaxed text-muted-foreground mb-3">
      Investimenti digitali retail al 4,7% del fatturato (+47% in un anno). Il 71% delle grandi imprese ha avviato progetti AI. Mercato AI italiano: €1,8 mld (+50% annuo).
    </p>
    <div className="my-6 overflow-hidden rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="gradient-bg">
            <th className="px-5 py-3 text-left font-semibold text-white font-display">Applicazione AI</th>
            <th className="px-5 py-3 text-left font-semibold text-white font-display">Impatto misurato</th>
            <th className="px-5 py-3 text-left font-semibold text-white font-display">Fonte</th>
          </tr>
        </thead>
        <tbody>
          {aiTable.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-secondary/50' : 'bg-background'}>
              <td className="px-5 py-3 font-medium text-foreground">{row.app}</td>
              <td className="px-5 py-3 text-muted-foreground font-semibold">{row.impatto}</td>
              <td className="px-5 py-3 text-muted-foreground font-semibold">{row.fonte}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="text-sm leading-relaxed text-muted-foreground">
      Il paradosso: ogni euro in tecnologia genera ritorno solo con persone capaci di utilizzarla. L'Italia resta sotto la media UE (59% vs 69% di iniziative AI concrete).
    </p>
  </div>
);

const Page6 = () => (
  <div className="px-10 py-8">
    <PageHeader page={6} />
    <h3 className="mb-2 text-base font-bold text-foreground font-display">7. La priorità dei CEO: le persone al primo posto</h3>
    <p className="text-sm leading-relaxed text-muted-foreground mb-3">
      L'indagine Mediobanca 2025 su 124 operatori fotografa una consapevolezza chiara: il capitale umano è la priorità numero uno.
    </p>
    <CeoPrioritiesChart />
    <p className="text-sm leading-relaxed text-muted-foreground">
      In un settore al 2,9% di EBIT e €10,7 mld di costo del lavoro, anche un miglioramento del 5% nell'efficienza delle decisioni sul capitale umano libera centinaia di milioni di euro di valore.
    </p>
  </div>
);

const Page7 = () => (
  <div className="px-10 py-8">
    <PageHeader page={7} />
    <h3 className="mb-2 text-base font-bold text-foreground font-display">8. Come si posiziona Skillvue</h3>
    <p className="text-sm leading-relaxed text-muted-foreground mb-4">
      Le sfide convergono: €10,7 mld di costo del lavoro, turnover al 13,3% (punte 35% under-30), costo per uscita €11.000–€110.000, fabbisogno di 574.000 lavoratori in cinque anni. In un contesto con margini al 2,9%, queste sono voci di conto economico.
    </p>
    <div className="my-5 rounded-xl bg-gradient-to-br from-[#e8690a]/5 via-[#8b5cf6]/5 to-[#8b5cf6]/10 border border-[#8b5cf6]/20 p-5">
      <p className="text-sm leading-relaxed text-foreground font-medium">
        Skillvue nasce da una visione precisa: un mondo in cui ogni decisione sui talenti, dall'assunzione alla promozione, fino alla trasformazione organizzativa, sia guidata da <strong className="gradient-text">intelligenza oggettiva</strong>. La piattaforma parte dalla scienza, con framework psicometrici validati resi scalabili attraverso l'intelligenza artificiale.
      </p>
    </div>
    <p className="text-sm leading-relaxed text-muted-foreground mb-3">Nella GDO, questo si traduce in impatti concreti:</p>
    <ul className="space-y-2 mb-5">
      {[
        "Riduzione del time-to-hire e del turnover precoce nella selezione ad alto volume.",
        "Dati oggettivi per le conferme contrattuali.",
        "Pipeline di successione strutturate per capi reparto e store manager.",
        "Analisi dei gap di competenze per formazione mirata e miglioramento del ROI formativo.",
      ].map((item, i) => (
        <li key={i} className="flex gap-3 text-sm text-muted-foreground">
          <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full gradient-bg" />
          {item}
        </li>
      ))}
    </ul>
    <p className="text-sm leading-relaxed text-muted-foreground">
      Ogni punto percentuale di turnover evitabile recuperato si traduce direttamente in margine protetto. Per i gruppi con centinaia di punti vendita, Skillvue significa applicare standard uniformi lungo tutta l'organizzazione, riducendo la distanza tra headquarter e rete.
    </p>
  </div>
);

const Page8 = () => (
  <div className="px-10 py-8">
    <PageHeader page={8} />
    <h3 className="mb-2 text-base font-bold text-foreground font-display">9. Conclusione</h3>
    <p className="text-sm leading-relaxed text-muted-foreground mb-5">
      La GDO italiana si trova a un punto di inflessione strutturale. I prossimi cinque anni saranno definiti dalla capacità di navigare cinque forze convergenti: stagnazione demografica, avanzata discount e MDD, accelerazione digitale, ondata ESG, e crisi strutturale del capitale umano.
    </p>
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 mb-5">
      {[
        { val: '25-28%', label: 'Quota discount 2030' },
        { val: '>€1 mld', label: 'Retail media' },
        { val: '>4,5%', label: 'Investimenti digitali / fatturato' },
        { val: '574K+', label: 'Fabbisogno lavoratori' },
      ].map((s, i) => (
        <div key={i} className="stat-grid rounded-xl bg-secondary p-4 text-center border border-border">
          <div className="text-xl font-bold gradient-text font-display">{s.val}</div>
          <div className="mt-1 text-[11px] text-muted-foreground leading-tight">{s.label}</div>
        </div>
      ))}
    </div>
    <div className="my-5 rounded-xl bg-gradient-to-br from-[#e8690a]/5 via-[#8b5cf6]/5 to-[#8b5cf6]/10 border border-[#8b5cf6]/20 p-5">
      <p className="text-sm leading-relaxed text-foreground font-medium italic text-center">
        "Il vero differenziatore competitivo dei prossimi cinque anni non sarà la tecnologia acquistata, ma la capacità di costruire e mantenere il capitale umano necessario ad attivarla."
      </p>
    </div>
    <div className="pt-4 border-t border-border">
      <h4 className="text-xs font-bold text-foreground font-display mb-1">Fonti</h4>
      <p className="text-[10px] text-muted-foreground leading-relaxed">
        NielsenIQ, Mediobanca, ISTAT, Circana, TEHA/ADM, Excelsior/Unioncamere, ManpowerGroup, WEF, Osservatorio Polimi, Deloitte, BCG, McKinsey, IAB Europe, Nomisma/SANA, Codacons, AGCM, Capgemini, Il Sole 24 Ore.
      </p>
    </div>
    <div className="mt-10 pt-4 border-t border-border text-center">
      <p className="text-xs text-muted-foreground">A cura di <span className="gradient-text font-bold">Skillvue</span></p>
    </div>
  </div>
);

export { Page5, Page6, Page7, Page8 };
